import { useId, useLayoutEffect, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Icon, type IconName } from './Icon'
import { useLeaveSheet } from './sheetMotion'

/**
 * A sheet with a header (an icon, a title and the standard close button) over a body, centered on a desktop and sliding up from the
 * bottom edge on a phone, in the shared sheet style (design/studio/sheet-kit.css). The date chooser and the Contexts notes sheets use it,
 * so every sheet of this kind closes the same way (apps/app/DESIGN.md, "Sheets"). Escape, the close button and a tap outside dismiss it.
 * The panel keeps `reminder-editor` only because the calendar's styles are scoped by it.
 * `dismissible={false}` holds the sheet open while something that must not be interrupted is under way (a deletion): Escape, a tap outside
 * and the close button do nothing until it is true again.
 */
export function SheetDialog({ title, icon, closeLabel, className, onClose, dismissible = true, children }: {
  title: string; icon: IconName; closeLabel: string; className?: string; onClose: () => void; dismissible?: boolean; children: ReactNode
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const leaveNow = useLeaveSheet(dialogRef, onClose)
  const leave = () => { if (dismissible) leaveNow() }
  useLayoutEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal()
    else dialog.setAttribute('open', '')
    return () => {
      if (!dialog.open) return
      if (typeof dialog.close === 'function') dialog.close()
      else dialog.removeAttribute('open')
    }
  }, [])
  const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key !== 'Escape') return
    event.preventDefault()
    event.stopPropagation()
    leave()
  }
  return createPortal(
    <dialog ref={dialogRef} className={['modal modal-bottom sm:modal-middle studio-sheet reminder-modal reminder-modal--standalone', className].filter(Boolean).join(' ')} aria-labelledby={titleId} onKeyDownCapture={handleKeyDown}
      onCancel={event => { event.preventDefault(); leave() }} onClick={event => { if (event.target === event.currentTarget) leave() }}>
      <section className="modal-box sheet-panel reminder-editor">
        <header className="sheet-header">
          <h2 id={titleId} className="sheet-title"><Icon name={icon} />{title}</h2>
          <button className="btn btn-ghost btn-square sheet-close" type="button" onClick={leave} disabled={!dismissible} aria-label={closeLabel}><Icon name="close" /></button>
        </header>
        <div className="reminder-editor__body">{children}</div>
      </section>
    </dialog>,
    document.body,
  )
}
